import React, { useEffect, useState } from 'react';
import { Alert, Button, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';

interface LeadPrePromptWidgetProps {
  leadId: string;
  onActionComplete?: () => void;
}

interface PrePrompt {
  id: string;
  title: string;
  message: string;
  prompt_type: string;
  priority: string;
  suggested_actions: Array<{
    label: string;
    action: string;
    data: any;
    variant?: string;
  }>;
  context: {
    ai_confidence: number;
    ai_reasoning: string;
  };
}

const LeadPrePromptWidget: React.FC<LeadPrePromptWidgetProps> = ({ 
  leadId, 
  onActionComplete 
}) => {
  const [prompts, setPrompts] = useState<PrePrompt[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeadPrompts();
  }, [leadId]);

  const fetchLeadPrompts = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/agent/preprompts?lead_id=${leadId}&status=pending`
      );
      setPrompts(response.data.prePrompts || []);
    } catch (error) {
      console.error('Error fetching lead pre-prompts:', error);
    }
  };

  const handleAction = async (prompt: PrePrompt, action: any) => {
    try {
      setLoading(true);

      // Record action
      await axiosInstance.post(`/api/agent/preprompts/${prompt.id}/action`, {
        action_type: action.action,
        description: action.label
      });

      // Remove from UI
      setPrompts(prev => prev.filter(p => p.id !== prompt.id));
      
      toast.success(`Action completed: ${action.label}`);
      
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error handling action:', error);
      toast.error('Failed to complete action');
    } finally {
      setLoading(false);
    }
  };

  if (prompts.length === 0) {
    return null;
  }

  const latestPrompt = prompts[0]; // Show the most recent

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Alert 
          variant={
            latestPrompt.priority === 'high' ? 'danger' : 
            latestPrompt.priority === 'medium' ? 'warning' : 'info'
          }
          className="lead-preprompt-widget shadow-sm"
        >
          <div className="d-flex align-items-start">
            <div className="me-3">
              {latestPrompt.prompt_type === 'action_taken' && '✅'}
              {latestPrompt.prompt_type === 'requires_human_approval' && '⚠️'}
              {latestPrompt.prompt_type === 'automation_error' && '❌'}
            </div>
            <div className="flex-fill">
              <Alert.Heading className="h6 mb-1">
                AI Recommendation
                {prompts.length > 1 && (
                  <Badge bg="secondary" pill className="ms-2">
                    +{prompts.length - 1} more
                  </Badge>
                )}
              </Alert.Heading>
              <p className="mb-2">{latestPrompt.message}</p>
              
              {latestPrompt.context.ai_reasoning && (
                <small className="text-muted d-block mb-2">
                  <strong>Analysis:</strong> {latestPrompt.context.ai_reasoning}
                </small>
              )}

              <div className="d-flex flex-wrap gap-2">
                {latestPrompt.suggested_actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    size="sm"
                    onClick={() => handleAction(latestPrompt, action)}
                    disabled={loading}
                  >
                    {action.label}
                  </Button>
                ))}
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setPrompts(prev => prev.filter(p => p.id !== latestPrompt.id));
                  }}
                  disabled={loading}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </Alert>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadPrePromptWidget;